import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SelectOutlined
} from "@ant-design/icons";
import { Button, Input, Tooltip } from "antd";
import { ReactNode } from "react";
import { useTranslate } from "../i18n";

export interface CrudSearch {
    placeholder: string;
    /** Current term, used as the field's initial value. */
    value?: string;
    onChange: (value: string) => void;
    /** Runs the query — these lists search on the server. */
    onSearch: () => void;
}

interface CrudToolbarProps {
    /** Something is selected, so the contextual actions are meaningful. */
    hasSelection: boolean;
    /** Names what is selected — a record, or "3 selected". */
    selectionLabel?: ReactNode;
    /** Drops the selection without touching the record. */
    onClearSelection?: () => void;
    /** Opens the selected record. */
    onEdit?: () => void;
    /** Destructive action on the selection; labelled by `deleteLabel`. */
    onDelete?: () => void;
    /** Defaults to "Delete" in the active language. */
    deleteLabel?: string;
    /** Extra contextual controls, for screens whose selection does something else. */
    selectionActions?: ReactNode;
    /** Omitted where the entity cannot be created from this list. */
    onAdd?: () => void;
    /** Defaults to "Add" in the active language. */
    addLabel?: string;
    onReload?: () => void;
    /** Controls that stay available whether or not there is a selection. */
    idleActions?: ReactNode;
    /** Server-side search, rendered on the leading edge of the bar. */
    search?: CrudSearch;
    /** What the bar says when nothing is selected and there is no search.
        Defaults to the generic "pick a row" prompt. */
    hint?: string;
}

/**
 * The action bar above every list.
 *
 * It has two states rather than one. Idle, it offers only what is always
 * available — refresh, add, search — and says how to get the rest. With a row
 * selected it becomes a contextual strip naming that row, so a destructive
 * button is never sitting on screen greyed out next to an invisible selection.
 */
export const CrudToolbar = ({
    hasSelection,
    selectionLabel,
    onClearSelection,
    onEdit,
    onDelete,
    deleteLabel,
    selectionActions,
    onAdd,
    addLabel,
    onReload,
    idleActions,
    search,
    hint
}: CrudToolbarProps) => {
    const t = useTranslate();

    return (
        <div className={`crud-bar${hasSelection ? " is-selected" : ""}`}>
            {hasSelection ? (
                <div className="crud-bar__selection">
                    <span className="crud-bar__dot" />
                    <span>{t("crud.selected")}</span>
                    <span className="crud-bar__selection-name">{selectionLabel}</span>
                </div>
            ) : search != null ? (
                <Input.Search
                    className="crud-bar__search"
                    allowClear
                    enterButton
                    placeholder={search.placeholder}
                    defaultValue={search.value}
                    onChange={event => search.onChange(event.target.value)}
                    onSearch={search.onSearch}
                />
            ) : (
                <div className="crud-bar__hint">
                    <SelectOutlined />
                    <span>{hint ?? t("crud.hint.default")}</span>
                </div>
            )}

            <div className="crud-bar__spacer" />

            <div className="crud-bar__actions">
                {hasSelection ? (
                    <>
                        {onClearSelection != null ? (
                            <Button type="text" onClick={onClearSelection}>
                                {t("common.clear")}
                            </Button>
                        ) : null}
                        {onDelete != null ? (
                            <Button danger icon={<DeleteOutlined />} onClick={onDelete}>
                                {deleteLabel ?? t("common.delete")}
                            </Button>
                        ) : null}
                        {onEdit != null ? (
                            <Button type="primary" ghost icon={<EditOutlined />} onClick={onEdit}>
                                {t("common.open")}
                            </Button>
                        ) : null}
                        {selectionActions}
                        <span className="crud-bar__divider" />
                    </>
                ) : null}

                {idleActions}

                {onReload != null ? (
                    <Tooltip title={t("common.refresh")}>
                        <Button
                            type="text"
                            shape="circle"
                            aria-label={t("common.refresh")}
                            icon={<ReloadOutlined />}
                            onClick={onReload}
                        />
                    </Tooltip>
                ) : null}
                {onAdd != null ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={onAdd}>
                        {addLabel ?? t("common.add")}
                    </Button>
                ) : null}
            </div>
        </div>
    );
};
